import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Text, Box, OrbitControls, useTexture } from "@react-three/drei";
import logoUrl from "../assets/logo.png";
import DateBox from "devextreme-react/date-box";
import "./RiskDashboard.css";
import PageShell from "../components/layout/PageShell";
import MarketRiskStressTests from "../components/market-risk/MarketRiskStressTests";
import SPMatrixTopGrid from "../components/positions/SPMatrixTopGrid";
import { GridFundingSensitivity, GridStressScenarios, FundingRow, StressRow } from "../components/common/fundingGrids";

import { baseUrl } from "../hooks/useFetch";
import useCobDate from "../hooks/useCobDate";

interface MarketRiskRow {
  standalone_var?: number;
  expected_tail_loss?: number;
  diversification_benefit?: number;
  risk_utilisation?: number;
  stressed_etl?: number;
}

interface CubeData {
  market_risk?: MarketRiskRow[];
  [key: string]: any;
}

interface FaceData {
  position: [number, number, number];
  rotation: [number, number, number];
  title: string;
  content: string[];
  showLogo?: boolean;
}

interface RiskCubeProps {
  autoRotate: boolean;
  cubeData: CubeData | null;
}

function isFiniteNumber(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x);
}

function fmtMM(x: unknown): string {
  if (!isFiniteNumber(x)) return "N/A";
  const mm = x / 1_000_000;
  const abs = Math.abs(mm);
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return `€${mm.toLocaleString(undefined, { maximumFractionDigits: decimals })}MM`;
}

function fmtPct(x: unknown): string {
  if (!isFiniteNumber(x)) return "N/A";
  const p = x * 100;
  const abs = Math.abs(p);
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return `${p.toLocaleString(undefined, { maximumFractionDigits: decimals })}%`;
}

function getMarketRiskBlock(cubeData: CubeData | null): { title: string; content: string[] } {
  const mr = cubeData?.market_risk?.[0] || null;

  const title = "Market Risk";

  const standaloneVar = mr?.standalone_var;
  const standaloneEtl = mr?.expected_tail_loss; // (your endpoint has expected_tail_loss)
  const divBenefit = mr?.diversification_benefit;
  const riskUtilisation = mr?.risk_utilisation;
  const stressedEtl = mr?.stressed_etl;

  return {
    title,
    content: [
      `1-day VaR (95%): ${fmtMM(standaloneVar)}`,
      `1-day ETL (95%): ${fmtMM(standaloneEtl)}`,
      `Diversification benefit: ${fmtPct(divBenefit)}`,
      `Risk Limit Util: ${fmtPct(riskUtilisation)}`,
      `Stressed ETL: ${fmtMM(stressedEtl)}`,
    ],
  };
}

const LogoPlane: React.FC = () => {
  const texture = useTexture(logoUrl);
  return (
    <mesh position={[0, 0, 0.07]}>
      <planeGeometry args={[5, 1.5]} />
      <meshBasicMaterial map={texture} transparent />
    </mesh>
  );
};

const RiskCube: React.FC<RiskCubeProps> = ({ autoRotate, cubeData }) => {
  const cubeRef = useRef<any>(null);

  useFrame((state) => {
    if (cubeRef.current && autoRotate) {
      cubeRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.3;
      cubeRef.current.rotation.y += 0.005;
    }
  });

  const marketRiskBlock = useMemo(() => getMarketRiskBlock(cubeData), [cubeData]);

  const faceData: FaceData[] = useMemo(
    () => [
      {
        position: [0, 0, 4.05],
        rotation: [0, 0, 0],
        title: marketRiskBlock.title,
        content: marketRiskBlock.content,
      },
      {
        position: [0, 0, -4.05],
        rotation: [0, Math.PI, 0],
        title: "",
        content: [],
        showLogo: true,
      },
      {
        position: [4.05, 0, 0],
        rotation: [0, Math.PI / 2, 0],
        title: "Credit Risk",
        content: [
          "Exposure at Default: €500MM",
          "Probability of default: 2.3%",
          "Loss given default: 40%",
          "Expected Loss: €275k",
          "Unexpected Loss: €1.7MM",
        ],
      },
      {
        position: [-4.05, 0, 0],
        rotation: [0, -Math.PI / 2, 0],
        title: "Counterparty Concentration",
        content: [
          "Rating HHI: 0.25 🟢",
          "Sector HHI: 0.34 🔴",
          "Region HHI: 0.28 🟡",
          "Top 5 Counterparties: 40%",
        ],
      },
      {
        position: [0, 4.05, 0],
        rotation: [-Math.PI / 2, 0, 0],
        title: "",
        content: [],
        showLogo: true,
      },
      {
        position: [0, -4.05, 0],
        rotation: [Math.PI / 2, 0, 0],
        title: "Other Risks",
        content: ["Climate Risk: Moderate", "Regulatory Change: Ongoing", "Model Risk: Medium"],
      },
    ],
    [marketRiskBlock]
  );

  return (
    <group ref={cubeRef}>
      <Box args={[8, 8, 8]}>
        <meshStandardMaterial
          attach="material"
          color="#33ccff"
          transparent
          opacity={0.08}
          wireframe={true}
          emissive="#33ccff"
          emissiveIntensity={0.6}
        />
      </Box>

      {faceData.map((face, index) => (
        <group key={index} position={face.position} rotation={face.rotation}>
          <Box args={[7.5, 7.5, 0.1]}>
            <meshStandardMaterial attach="material" color="#0050a0" transparent opacity={0.9} />
          </Box>

          <Box args={[7.7, 7.7, 0.05]} position={[0, 0, -0.05]}>
            <meshStandardMaterial
              attach="material"
              color="#00bfff"
              transparent
              opacity={0.3}
              emissive="#00bfff"
              emissiveIntensity={0.2}
            />
          </Box>

          {face.showLogo ? (
            <LogoPlane />
          ) : (
            <>
              <Text
                position={[0, 2.8, 0.06]}
                fontSize={0.5}
                color="#e0f7ff"
                anchorX="center"
                anchorY="middle"
                maxWidth={7}
              >
                {face.title}
              </Text>

              {face.content.map((line, lineIndex) => (
                <Text
                  key={lineIndex}
                  position={[0, 2 - lineIndex * 0.7, 0.06]}
                  fontSize={0.35}
                  color="white"
                  anchorX="center"
                  anchorY="middle"
                  maxWidth={7}
                >
                  {line}
                </Text>
              ))}
            </>
          )}
        </group>
      ))}
    </group>
  );
};

const RiskDashboard: React.FC = () => {
  const [cobDate, setCobDate] = useCobDate("/pnl_latest_cob"); //funding_dashboard_latest_cob
  const [autoRotate, setAutoRotate] = useState<boolean>(true);
  const [cubeData, setCubeData] = useState<CubeData | null>(null);
  const [fundingSensitivity, setFundingSensitivity] = useState<FundingRow[]>([]);
  const [fundingStress, setFundingStress] = useState<StressRow[]>([]);

  const onCobDateChanged = useCallback((nextDate: Date | null | undefined) => {
    if (nextDate instanceof Date && !Number.isNaN(nextDate.getTime())) {
      setCobDate(nextDate);
      return;
    }
    if (nextDate === null || nextDate === undefined) {
      setCobDate(null);
    }
  }, []);

  const cobIso: string | null =
    cobDate instanceof Date && !Number.isNaN(cobDate.getTime())
      ? cobDate.toISOString().slice(0, 10)
      : null;

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    const loadCube = async (): Promise<void> => {
      if (!cobIso) {
        setCubeData(null);
        return;
      }

      try {
        const res = await fetch(`${baseUrl}/cube?cob_date=${encodeURIComponent(cobIso)}`, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: CubeData = await res.json();
        if (!cancelled) setCubeData(json);
      } catch (e: any) {
        if (!cancelled) setCubeData(null);
        if (e?.name !== "AbortError") console.error(e);
      }
    };

    loadCube();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [cobIso]);

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    const loadFunding = async (): Promise<void> => {
      if (!cobIso) {
        setFundingSensitivity([]);
        setFundingStress([]);
        return;
      }
      try {
        const compareDateStr = `${new Date().getFullYear() - 1}-12-31`;
        const res = await fetch(
          `${baseUrl}/funding_dashboard?cob_date=${encodeURIComponent(cobIso)}&compare_date=${encodeURIComponent(compareDateStr)}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setFundingSensitivity(data?.left?.funding_sensitivity_by_asset_class ?? []);
          setFundingStress(data?.left?.stress_test_scenarios ?? []);
        }
      } catch (e: any) {
        if (!cancelled) {
          setFundingSensitivity([]);
          setFundingStress([]);
        }
        if (e?.name !== "AbortError") console.error(e);
      }
    };

    loadFunding();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [cobIso]);

  return (
    <PageShell>
      <div className="risk-grid">
        <div className="grid-cob cob-pill">
          <div className="cob-section">
            <label className="cob-label">CoB:</label>
            <DateBox
              value={cobDate}
              type="date"
              displayFormat="dd/MM/yyyy"
              useMaskBehavior={true}
              onValueChanged={(e: any) => onCobDateChanged(e.value)}
              elementAttr={{ class: "date-input date-input-small" }}
            />
          </div>
        </div>

        <div className="grid-structured panel">
          <h3>Structured Products desk - P&amp;L impact (€'000s)</h3>
          <div className="grid-wrap">
            <SPMatrixTopGrid cobStr={cobIso} />
          </div>
        </div>

        <div className="grid-cube">
          <div className="cube-container">
            <Canvas camera={{ position: [0, 0, 20], fov: 50 }}>
              <ambientLight intensity={0.4} />
              <directionalLight position={[5, 5, 5]} intensity={0.6} />
              <pointLight position={[-5, -5, 5]} intensity={0.4} color="#00bfff" />
              <pointLight position={[5, -5, -5]} intensity={0.3} color="#0080ff" />

              <group position={[0, 0, 0]} scale={[1.15, 1.15, 1.15]}>
                <RiskCube autoRotate={autoRotate} cubeData={cubeData} />
              </group>

              <OrbitControls
                enablePan={false}
                enableZoom={true}
                minDistance={8}
                maxDistance={22}
                rotateSpeed={0.6}
              />
            </Canvas>

            <button className="cube-toggle" type="button" onClick={() => setAutoRotate((p) => !p)}>
              {autoRotate ? "Pause rotation" : "Auto-rotate"}
            </button>

            <div className="capital-overlay panel">
              <h3>Capital &amp; Regulatory Requirements</h3>
              <p>K-Factor Requirement: €53MM</p>
              <p>Own Funds: €200MM</p>
              <p>Buffer: €147MM 🟢</p>
            </div>
          </div>
        </div>

        <div className="grid-funding panel">
          <div className="funding-stack">
            <GridFundingSensitivity title="Funding Sensitivity by Asset Class" rows={fundingSensitivity} />
            <GridStressScenarios title="Funding Stress Tests" rows={fundingStress} />
            <div className="panel panel-stress stress-inlined">
              <h3>Historic Stress Tests</h3>
              <div className="grid-wrap">
                <MarketRiskStressTests cobDate={cobDate} cols={["stress_name", "pnl"]} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default RiskDashboard;
